import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { DateRange } from 'react-day-picker';
import { PrimeTimesGrid } from './PrimeTimesGrid';
import { PremiumPeakCards } from './general-data/PremiumPeakCards';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WEEKDAYS, HOUR_BUCKETS, PrimeTimeMetric } from '../hooks/types/primeTimesTypes';
import { transformPrimeTimesToMatrix } from './utils/transformPrimeTimes';

interface GeneralDataViewProps {
  metric: PrimeTimeMetric;
  date?: DateRange;
  videoId: string | null;
  metricsEnhanced?: any;
}

export function GeneralDataView({ metric, date, videoId, metricsEnhanced }: GeneralDataViewProps) {
  useLanguage();
  const primeTimes = metricsEnhanced?.primeTimes;
  const isLoading = metricsEnhanced?.isLoading ?? false;

  // Transform backend data to PrimeTimesMatrix format if available
  const data = primeTimes ? transformPrimeTimesToMatrix(primeTimes) : null;

  const handleExportCSV = () => {
    if (!data) return;
    
    const csvRows = [
      ['Dia', 'Hora', 'Plays Únicos', 'Visualizações Únicas'].join(',')
    ];

    data.matrix.forEach(cell => {
      const weekday = WEEKDAYS.find(w => w.index === cell.weekday);
      const bucket = HOUR_BUCKETS.find(b => b.index === cell.hourBucket);

      csvRows.push([
        weekday?.fullLabel || '',
        bucket?.label || '',
        cell.playsUnique.toString(),
        cell.viewsUnique.toString()
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `horarios-pico-${metric}-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('peaks_export_csv', { mode: 'general', metric });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ minHeight: 320 }}>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EBF4FF] flex items-center justify-center">
          <Clock className="h-8 w-8 text-[#2979FF]" />
        </div>
        <h3 className="text-lg font-semibold text-[#101828] mb-2">
          {!videoId ? translateInterfaceText("Nenhum vídeo selecionado") : translateInterfaceText("Sem dados de horários no período")}
        </h3>
        <p className="text-[#667085] text-sm max-w-[280px] mx-auto text-center">
          {!videoId
            ? translateInterfaceText("Selecione um vídeo para visualizar os horários de pico.")
            : translateInterfaceText("Tente ampliar o período ou visualizar outro vídeo.")
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 3 Cards premium superiores */}
      <PremiumPeakCards data={data} metric={metric} />

      {/* Heatmap */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[16px] font-semibold text-[#0F172A]">{translateInterfaceText("Mapa de Calor (Volume)")}</h3>
            <p className="text-[13px] font-normal text-[#6B7280]">
              {metric === 'plays' ? translateInterfaceText("Plays únicos") : translateInterfaceText("Visualizações únicas")} {" "}{translateInterfaceText("por dia e horário")}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportCSV}
            className="hover:bg-[#F3F7FF] hover:border-[#93C5FD] transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            {translateInterfaceText("Exportar CSV")}</Button>
        </div>

        <div className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
          <PrimeTimesGrid data={data} metric={metric} />
        </div>
      </div>

      {/* Info tooltip */}
      <div className="p-4 rounded-lg bg-[#F3F7FF] border border-[#D4E1FF]">
        <p className="text-[13px] font-normal text-[#344054]">
          💡 <span className="font-medium">{translateInterfaceText("Dica:")}</span> {" "}{translateInterfaceText("As cores mais intensas indicam horários com maior volume de")}{' '}
          {metric === 'plays' ? translateInterfaceText("plays únicos") : translateInterfaceText("visualizações únicas")}{translateInterfaceText(". Use esses dados para planejar estratégias de tráfego.")}</p>
      </div>
    </div>
  );
}
